import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Paper,
    IconButton,
    Button,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    FormHelperText,
    InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import { updateProjectChannelOptions } from "../../../../api/endpoints";
import { ChannelGroup } from "../../../../hooks/useChannelGroups";

interface OptionRow {
    key: string;
    optionId: number | "";
    valueId: number | "";
    numericValue: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    projectId: number;
    channel: ChannelGroup["channel"];
    /** Called after a successful save — parent refetches the project. */
    onSaved: () => void;
}

const LABEL_MAX = 80;

/**
 * Edits ONE project-channel instance's label (shown beside "PDP #2") and its
 * option-value selections (the per-project overrides of the channel's
 * defaults). Mirrors the row UX of
 * the admin ContentChannelModal but writes to
 * PATCH /brand-projects/:projectId/channels/:brandProjectChannelId.
 */
export const ProjectChannelOptionsModal: FC<Props> = ({
    open,
    onClose,
    projectId,
    channel,
    onSaved,
}) => {
    const [channelOptions, setChannelOptions] = useState<any[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [rows, setRows] = useState<OptionRow[]>([]);
    const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    const [label, setLabel] = useState("");

    const hydrateRows = () => {
        const selections = channel.project_option_selections || [];
        setRows(
            selections.map((sel: any, i: number) => ({
                key: `sel-${sel.content_channel_option_value_id ?? i}-${i}`,
                optionId: sel.option_value?.option?.id ?? "",
                valueId:
                    sel.option_value?.id ??
                    sel.content_channel_option_value_id ??
                    "",
                numericValue:
                    sel.numeric_value === null ||
                    sel.numeric_value === undefined
                        ? ""
                        : String(sel.numeric_value),
            }))
        );
    };

    useEffect(() => {
        if (!open) return;
        setFormError("");
        setRowErrors({});
        setLabel(channel.instance_label || "");
        hydrateRows();
        setLoadingOptions(true);
        axiosInstant
            .get("/content-channel-options")
            .then((response) => {
                const data = Array.isArray(response.data)
                    ? response.data
                    : response.data?.rows || [];
                setChannelOptions(data);
            })
            .catch((err) => {
                console.error("Failed to fetch channel options:", err);
                setChannelOptions([]);
            })
            .finally(() => setLoadingOptions(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, channel.brand_project_channel_id]);

    const selectedPairs = useMemo(
        () =>
            new Set(
                rows
                    .filter((r) => r.optionId && r.valueId)
                    .map((r) => `${r.optionId}:${r.valueId}`)
            ),
        [rows]
    );

    const updateRow = (index: number, patch: Partial<OptionRow>) => {
        setRows((prev) =>
            prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
        );
        setRowErrors((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                optionId: "",
                valueId: "",
                numericValue: "",
            },
        ]);
    };

    const removeRow = (index: number) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
        setRowErrors((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const applyServerError = (err: any) => {
        const data = err?.response?.data;
        // control() errors serialize as bare JSON strings ("InvalidNumericValue")
        const code =
            typeof data === "string"
                ? data
                : data?.code || data?.error || data?.errorCode;
        if (code === "InvalidNumericValue") {
            setFormError("One of the numeric values is not a finite number.");
        } else if (code === "UnknownOptionValueId") {
            setFormError("One of the selected option values no longer exists.");
        } else {
            setFormError("Failed to save channel options. Please try again.");
        }
    };

    const handleSave = async () => {
        if (channel.brand_project_channel_id == null) return;
        setFormError("");

        const nextRowErrors: Record<number, string> = {};
        rows.forEach((r, i) => {
            if (r.numericValue !== "" && !Number.isFinite(Number(r.numericValue))) {
                nextRowErrors[i] = "Must be a finite number";
            }
        });
        if (Object.keys(nextRowErrors).length > 0) {
            setRowErrors(nextRowErrors);
            return;
        }

        const selections = rows
            .filter((r) => r.valueId)
            .map((r) => ({
                content_channel_option_value_id: Number(r.valueId),
                numeric_value:
                    r.numericValue === "" ? null : Number(r.numericValue),
            }));

        setSaving(true);
        try {
            await axiosInstant.patch(
                updateProjectChannelOptions(
                    projectId,
                    channel.brand_project_channel_id
                ),
                { selections, label: label.trim() || null }
            );
            toast.success("Channel updated");
            onSaved();
            onClose();
        } catch (err) {
            console.error("Failed to save channel options:", err);
            applyServerError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                component={Paper}
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "calc(100% - 24px)", sm: 560 },
                    maxWidth: 560,
                    maxHeight: { xs: "calc(100vh - 24px)", sm: "85vh" },
                    bgcolor: "var(--surface)",
                    borderRadius: "12px",
                    boxShadow: 24,
                    padding: { xs: "16px", sm: "24px" },
                    overflow: "auto",
                }}
            >
                <Box className="flex flex-col gap-4">
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography sx={{ fontSize: "24px", fontWeight: 900 }}>
                            {channel.name} Options
                        </Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <TextField
                        size="small"
                        label="Label"
                        placeholder="e.g. Holiday unboxing"
                        value={label}
                        onChange={(e) => setLabel(e.target.value.slice(0, LABEL_MAX))}
                        helperText="Shown beside the channel name everywhere, including the brand's downloads."
                        fullWidth
                    />

                    <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>
                        These options apply to this project only. Channel
                        defaults are managed in Admin &gt; Pricing.
                    </Typography>

                    {loadingOptions && (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={20} />
                        </Box>
                    )}

                    {formError && (
                        <FormHelperText error>{formError}</FormHelperText>
                    )}

                    <Box display="flex" flexDirection="column" gap={1.5}>
                        {rows.map((row, index) => {
                            const selectedOption = channelOptions.find(
                                (o) => o.id === Number(row.optionId)
                            );
                            const values = selectedOption?.values || [];
                            const showNumericValue =
                                selectedOption?.type === "value";
                            return (
                                <Box
                                    key={row.key}
                                    display="flex"
                                    gap={1}
                                    alignItems="flex-start"
                                >
                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 160, flex: 1 }}
                                    >
                                        <InputLabel>Option</InputLabel>
                                        <Select
                                            label="Option"
                                            value={row.optionId || ""}
                                            onChange={(e) =>
                                                updateRow(index, {
                                                    optionId: e.target
                                                        .value as number,
                                                    valueId: "",
                                                })
                                            }
                                        >
                                            {channelOptions.map((opt) => (
                                                <MenuItem
                                                    key={opt.id}
                                                    value={opt.id}
                                                >
                                                    {opt.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 160, flex: 1 }}
                                        disabled={!row.optionId}
                                    >
                                        <InputLabel>Value</InputLabel>
                                        <Select
                                            label="Value"
                                            value={row.valueId || ""}
                                            onChange={(e) =>
                                                updateRow(index, {
                                                    valueId: e.target
                                                        .value as number,
                                                })
                                            }
                                        >
                                            {values.map((v: any) => {
                                                const pairKey = `${row.optionId}:${v.id}`;
                                                const isCurrent =
                                                    Number(row.valueId) ===
                                                    Number(v.id);
                                                const isUsed =
                                                    !isCurrent &&
                                                    selectedPairs.has(pairKey);
                                                return (
                                                    <MenuItem
                                                        key={v.id}
                                                        value={v.id}
                                                        disabled={isUsed}
                                                    >
                                                        {v.name}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>

                                    {showNumericValue && (
                                        <FormControl
                                            size="small"
                                            sx={{ width: 130 }}
                                            error={!!rowErrors[index]}
                                        >
                                            <TextField
                                                size="small"
                                                label="Value"
                                                type="number"
                                                inputProps={{ step: "any" }}
                                                value={row.numericValue}
                                                onChange={(e) =>
                                                    updateRow(index, {
                                                        numericValue:
                                                            e.target.value,
                                                    })
                                                }
                                                error={!!rowErrors[index]}
                                                InputProps={
                                                    selectedOption?.unit
                                                        ? {
                                                              endAdornment: (
                                                                  <InputAdornment position="end">
                                                                      {
                                                                          selectedOption.unit
                                                                      }
                                                                  </InputAdornment>
                                                              ),
                                                          }
                                                        : undefined
                                                }
                                            />
                                            {rowErrors[index] && (
                                                <FormHelperText error>
                                                    {rowErrors[index]}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    )}

                                    <IconButton
                                        aria-label="Remove option"
                                        onClick={() => removeRow(index)}
                                        size="small"
                                        sx={{ mt: "4px" }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>

                    <Button
                        type="button"
                        onClick={addRow}
                        startIcon={<AddIcon />}
                        size="small"
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                        disabled={loadingOptions}
                    >
                        Add option
                    </Button>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Button onClick={onClose} type="button">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving || loadingOptions}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};
